import { Stack } from '@/components/layout';
import { Loading } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { PlayerFiltersProvider } from '@/features/player/context/player-filters-context';
import { PlayerSortProvider } from '@/features/player/context/player-sort-context';
import { useScaling } from '@/hooks';
import { ComposeProviders, provider } from '@/providers';
import { colors } from '@/theme/colors';
import React, { Activity, ReactNode, useMemo, useRef, useState } from 'react';
import { useSharedValue } from 'react-native-reanimated';

import {
  PlayerListTemplateListRef,
  PlayerListTemplateProvider,
  type PlayerListTemplateContextValue,
} from './context';

/**
 * Hoists the player filter + sort providers so that any child can consume their
 * shared state (e.g. persisted filter selection, sort preference) without each
 * screen having to wire up the provider boilerplate.
 *
 * Usage:
 * ```tsx
 * <PlayerListTemplateV2.Providers userUid={uid}>
 *   <ScreenContent />
 * </PlayerListTemplateV2.Providers>
 * ```
 */
export function Providers({
  userUid,
  children,
}: {
  userUid: string | undefined;
  children: ReactNode;
}) {
  return (
    <ComposeProviders
      providers={[
        provider(PlayerFiltersProvider, { key: userUid ?? 'guest' }),
        provider(PlayerSortProvider, { key: userUid ?? 'guest' }),
      ]}>
      {children}
    </ComposeProviders>
  );
}

export type PlayerListTemplateV2RootProps<T extends { id: string }> = {
  /** Result of a list logic hook (e.g. `useListPlayer`, `useOverviewSessions`). */
  listLogic: player.ListPlayerLogic<T>;
  loadingText?: string;
  /** Default `SearchBar` placeholder when slot doesn't override it. */
  searchPlaceholder?: string;
  skipHideActionButtonOnScroll?: boolean;
  children: ReactNode;
};

export function Root<T extends { id: string } = player.PlayerLookupData>({
  listLogic,
  loadingText,
  searchPlaceholder,
  skipHideActionButtonOnScroll = false,
  children,
}: PlayerListTemplateV2RootProps<T>) {
  const { scale } = useScaling();
  const scrollY = useSharedValue(0);
  const buttonCreateProfileHeight = useSharedValue(0);
  const [headerActionMeasuredHeight, setHeaderActionMeasuredHeight] = useState(0);
  const [hasActionButton, setHasActionButton] = useState(false);
  const listRef = useRef<PlayerListTemplateListRef>(null);

  const ctxValue = useMemo<PlayerListTemplateContextValue>(
    () => ({
      listLogic: listLogic as unknown as player.ListPlayerLogic<{ id: string }>,
      scrollY,
      buttonCreateProfileHeight,
      headerActionMeasuredHeight,
      setHeaderActionMeasuredHeight,
      hasActionButton,
      setHasActionButton,
      listRef,
      skipHideActionButtonOnScroll,
      searchPlaceholder: searchPlaceholder ?? STRINGS.player.listPlayers.searchPlaceholder,
    }),
    [
      listLogic,
      scrollY,
      buttonCreateProfileHeight,
      headerActionMeasuredHeight,
      hasActionButton,
      skipHideActionButtonOnScroll,
      searchPlaceholder,
    ],
  );

  const { isFetching, isLoading } = listLogic;
  const loadingVisible = isFetching || isLoading;

  return (
    <PlayerListTemplateProvider value={ctxValue}>
      <Stack flex={1} backgroundColor={colors['2']}>
        {children}
      </Stack>
      <Activity mode={loadingVisible ? 'visible' : 'hidden'}>
        <Stack
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          backgroundColor="rgba(0, 0, 0, 0.5)"
          justify="center"
          zIndex={1000}
          align="center">
          <Stack
            direction="column"
            minWidth={scale(140)}
            paddingY={scale(16)}
            paddingX={scale(16)}
            borderRadius={scale(12)}
            backgroundColor={colors['Backgrounds/Primary']}>
            <Loading text={loadingText ?? STRINGS.player.listPlayers.loadingPlayers} />
          </Stack>
        </Stack>
      </Activity>
    </PlayerListTemplateProvider>
  );
}
